/* eslint max-depth: ["error", 10] */
import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import {
    type GetCellNameParams,
    getCellName,
} from './appletUtil';

type AppletEventDefSource = {
    kind: 'user';
} | {
    kind: 'btn';
    cellId: string;
} | {
    kind: 'workInstruction';
};

export const workInstructionInstanceStepCompleteEventId = 'work-instruction-instance-step-complete';

/** centralized place to define how implied applet event ids are generated */
export const impliedAppletEventIds = {
    btnClick: (opts: { cellId: string }) => `${opts.cellId}:click`,
};

/** centralized place to define how implied applet event names are generated */
export const impliedAppletEventNames = {
    btnClick: (payload: GetCellNameParams<Latest.Screen.Cells.Btn.Cell>) => {
        const btnCellName = getCellName(payload);
        return `${btnCellName} clicked`;
    },
};

export type GetAppletEventsResult = Record<string, {
    def: Latest.AppletEvent.Def;
    source: AppletEventDefSource;
    // references: unknown[]; // FUTURE?
}>;
export function getImpliedAppletEvents (appletDesignVersionCfg: Latest.AppletDesignVersionConfig): GetAppletEventsResult {
    const result: GetAppletEventsResult = {};

    // implied events
    for (const screen of appletDesignVersionCfg.screens) {
        for (const cell of screen.cells) {
            if (cell.type === 'btn') {
                const noExplicitEvent = !cell.attrs.appletEventId;
                if (noExplicitEvent) {
                    const appletEventId = impliedAppletEventIds.btnClick({ cellId: cell.id });
                    const appletEventName = impliedAppletEventNames.btnClick({
                        cell,
                        defaultText: 'Unnamed Button',
                        distinguishableIdentifier: 'label',
                        designConfig: appletDesignVersionCfg,
                    });
                    result[appletEventId] = {
                        def: { appletEventId, appletEventName },
                        source: { kind: 'btn', cellId: cell.id },
                    };
                }
            }
        }
    }

    if (Object.values(appletDesignVersionCfg.flows).find(f => Object.values(f.nodes).find(n => n.kind === 'workInstruction'))) {
        result[workInstructionInstanceStepCompleteEventId] = {
            def: {
                appletEventName: 'Complete Work Instruction Step',
                appletEventId: workInstructionInstanceStepCompleteEventId,
                description: 'Event emitted when a the current step of a bound work instruction instance should be completed',
            },
            source: {
                kind: 'workInstruction',
            },
        };
    }

    return result;
}
/**
 * @param appletDesignCfg The entire applet design config
 *
 * By making this function the source of truth of allowed applet events,
 * we ensure that all possible locations for applet events are touched here.
 * No need for zod schema hooks which are unreliably used.
 */
export function getAppletEvents (appletDesignVersionCfg: Latest.AppletDesignVersionConfig): GetAppletEventsResult {
    const result: GetAppletEventsResult = {};

    // user defined applet events
    for (const appletEventDef of Object.values(appletDesignVersionCfg.userAppletEvents)) {
        result[appletEventDef.appletEventId] = {
            def: appletEventDef,
            source: { kind: 'user' },
        };
    }
    const impliedEvents = getImpliedAppletEvents(appletDesignVersionCfg);
    for (const [ appletEventId, appletEvent ] of Object.entries(impliedEvents)) {
        result[appletEventId] = appletEvent;
    }


    return result;
}
